import { Component, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonButtonRowWidget } from '@eforall/common';
import { DialogService } from '@eforall/dashboard';
import { LanguageId } from '@interfaces';
import { AppService, FuncService } from '../../../../../services';
import { VideoDialog, VideoDialogData } from '../../../dialog/video-dialog/video.dialog';
import { VolunteerDialogDataService } from '../volunteer-dialog-data.service';

@Component({
	selector: 'volunteer-management-dialog-part',
	standalone: true,
	imports: [
		CommonButtonRowWidget,
	],
	templateUrl: './management-dialog.part.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './management-dialog.part.scss'
})
export class VolunteerManagementDialogPart {

	public readonly dialogDataService = inject(VolunteerDialogDataService);
	private readonly func = inject(FuncService);
	private readonly dialogService = inject(DialogService);
	private app = inject(AppService);

	public readonly canManageVolunteer = computed(() => {
		const permissions = this.app.data().permissions;
		return permissions.isSoftwareDev || permissions.canManageVolunteerStaff;
	});

	public readonly isResetting = signal<boolean>(false);
	public readonly isSwitchingLanguage = signal<boolean>(false);
	public readonly isDeleting = signal<boolean>(false);

	public readonly volunteerDetails = computed(() => {
		return this.dialogDataService.volunteerDetails();
	});

	public readonly hasEnglishVideo = computed(() => {
		return this.volunteerDetails()?.hasEnVideo ?? false;
	});

	public readonly hasSpanishVideo = computed(() => {
		return this.volunteerDetails()?.hasEsVideo ?? false;
	});

	public async openVideoDialog(languageId: LanguageId) {
		const volunteer = this.volunteerDetails();
		if (!volunteer || !volunteer.person.id) return;

		const dialogData: VideoDialogData = {
			type: 'volunteer',
			personId: volunteer.person.id,
			uniqueVideoId: languageId
		};
		this.dialogService.showCustom(VideoDialog, { data: dialogData }, 500, 550);
	}

	public async resetVideo(languageId: LanguageId) {
		const languageName = languageId === LanguageId.English ? 'English' : 'Spanish';

		const yes = await this.dialogService.confirm(`Are you sure you want to reset the ${languageName} video? This will set has${languageId === LanguageId.English ? 'En' : 'Es'}Video to 'N'. The video file will remain in storage.`, 'Yes', 'No', [], 400, 250);

		if (!yes) return;

		this.isResetting.set(true);
		try {
			const volunteer = this.volunteerDetails();
			if (!volunteer?.volunteerId || !volunteer?.person?.id) {
				console.error('No volunteerId or personId found');
				return;
			}

			await this.func.volunteer.resetVideo({
				volunteerId: volunteer.volunteerId,
				personId: volunteer.person.id,
				languageId,
				isDelete: false
			});
			await this.dialogDataService.getVolunteerDetails(volunteer.volunteerId);
		} catch (error) {
			console.error('Error resetting video:', error);
			await this.dialogService.showMessage('Error', 'Failed to reset video. Please try again.', 'Okay', [], 400, 200);
		} finally {
			this.isResetting.set(false);
		}
	}

	public async switchLanguage(fromLanguageId: LanguageId) {
		const fromLanguageName = fromLanguageId === LanguageId.English ? 'English' : 'Spanish';
		const toLanguageName = fromLanguageId === LanguageId.English ? 'Spanish' : 'English';

		const yes = await this.dialogService.confirm(`Are you sure you want to switch the ${fromLanguageName} video to ${toLanguageName}? This will change the video language designation for this volunteer.`, 'Yes', 'No', [], 400, 250);

		if (!yes) return;

		this.isSwitchingLanguage.set(true);
		try {
			const volunteerId = this.volunteerDetails()?.volunteerId;
			if (!volunteerId) {
				console.error('No volunteerId found');
				return;
			}

			const toLanguageId = fromLanguageId === LanguageId.English ? LanguageId.Espanol : LanguageId.English;

			await this.func.volunteer.updateVolunteerSwitchLanguage({ volunteerId, fromLanguageId, toLanguageId });
			await this.dialogDataService.getVolunteerDetails(volunteerId);
		} catch (error) {
			console.error('Error switching video language:', error);
			await this.dialogService.showMessage('Error', 'Failed to switch video language. Please try again.', 'Okay', [], 400, 200);
		} finally {
			this.isSwitchingLanguage.set(false);
		}
	}

	public async deleteVideo(languageId: LanguageId) {
		const languageName = languageId === LanguageId.English ? 'English' : 'Spanish';

		const yes = await this.dialogService.confirm(`Are you sure you want to delete the ${languageName} video? This action cannot be undone. The video file will be permanently removed from storage.`, 'Yes', 'No', [], 400, 250);

		if (!yes) return;

		this.isDeleting.set(true);
		try {
			const volunteer = this.volunteerDetails();
			if (!volunteer?.volunteerId || !volunteer?.person?.id) {
				console.error('No volunteerId or personId found');
				return;
			}

			await this.func.volunteer.resetVideo({
				volunteerId: volunteer.volunteerId,
				personId: volunteer.person.id,
				languageId,
				isDelete: true
			});
			await this.dialogDataService.getVolunteerDetails(volunteer.volunteerId);
		} catch (error) {
			console.error('Error deleting video:', error);
			await this.dialogService.showMessage('Error', 'Failed to delete video. Please try again.', 'Okay', [], 400, 200);
		} finally {
			this.isDeleting.set(false);
		}
	}

	// Expose language IDs as computed values for template use
	public readonly englishLanguageId = LanguageId.English;
	public readonly spanishLanguageId = LanguageId.Espanol;
}
